import React, { MouseEventHandler, useCallback, useMemo, useState, useRef, useEffect } from 'react';

import { useSnackbar } from 'notistack';
import { faQuestion } from '@fortawesome/pro-solid-svg-icons';
import { IconButton, IconButtonTypeMap, Tooltip, TooltipProps, useTheme } from '@mui/material';
import { FaOptionIcon, FaOptionIconProps, NamedIconsType } from '@europrocurement/l2d-icons';
import { handleError } from '@europrocurement/l2d-utils';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

export type FlexyIconButtonProps = {
    icon?: IconType;
    iconOverwriteProps?: Omit<FaOptionIconProps, 'icon'>;
    buttonOverwriteProps?: IconButtonTypeMap<object, 'button'>['props'];
    tooltipOverwriteProps?: Omit<TooltipProps, 'children'>;
    displayTooltip?: boolean;
    disabled?: boolean;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    confirmation?: boolean;
    confirmationIcon?: IconType;
    confirmationTooltip?: string;
    confirmationTimeout?: number;
    minimumProcessingDelay?: number;
    showProcessingState?: boolean;
    processingIcon?: IconType;
    confirmationAnimation?: boolean;
    confirmationHoverColor?: string;
    normalHoverColor?: string;
};

// Union type pour accepter les différents types d'icônes
export type IconType = NamedIconsType | IconProp;

// Détection du type d'icône
const isNamedIcon = (icon: IconType): icon is NamedIconsType =>
    typeof icon === 'object' &&
    icon !== null &&
    'name' in icon &&
    'displayName' in icon &&
    'props' in icon;

// Détection si c'est un IconProp valide
const isIconProp = (icon: IconType): icon is IconProp => {
    if (isNamedIcon(icon)) return false;

    // String (IconName)
    if (typeof icon === 'string') return true;

    // Array [prefix, name]
    if (Array.isArray(icon) && icon.length === 2) return true;

    // IconLookup object
    if (typeof icon === 'object' && icon !== null) {
        return 'prefix' in icon && 'iconName' in icon;
    }

    return false;
};

// Adaptation des props selon le type d'icône
const adaptIconProps = (
    icon: IconType,
): {
    adaptedIcon: IconType;
    faOptionIconProps: Omit<FaOptionIconProps, 'icon'> & { icon: IconProp };
} => {
    if (isNamedIcon(icon)) {
        // NamedIconsType : utilisation directe
        return {
            adaptedIcon: icon,
            faOptionIconProps: {
                ...(icon.props || {}),
                icon: icon.props?.icon || faQuestion,
            },
        };
    }
    if (isIconProp(icon)) {
        // IconProp : adaptation pour FaOptionIcon
        return {
            adaptedIcon: icon,
            faOptionIconProps: {
                icon, // FaOptionIcon accepte directement les IconProp
            },
        };
    }
    // Fallback : icône par défaut
    return {
        adaptedIcon: {
            name: 'undefined',
            displayName: 'Non défini',
            props: { icon: faQuestion },
        },
        faOptionIconProps: {
            icon: faQuestion,
        },
    };
};

export const FlexyIconButton = React.forwardRef<HTMLButtonElement, FlexyIconButtonProps>(
    (
        {
            iconOverwriteProps,
            buttonOverwriteProps,
            tooltipOverwriteProps,
            icon = {
                name: 'undefined',
                displayName: 'Non défini',
                props: { icon: faQuestion },
            },
            displayTooltip = false,
            disabled = false,
            onClick = () => {},
            confirmation = false,
            confirmationIcon = ['fasl', 'exclamation-circle'] as IconProp,
            confirmationTooltip = 'Cliquez pour confirmer',
            confirmationTimeout = 3000,
            minimumProcessingDelay = 500,
            showProcessingState = true,
            processingIcon = ['fasl', 'spinner'] as IconProp,
            confirmationAnimation = true,
            confirmationHoverColor,
            normalHoverColor,
        }: FlexyIconButtonProps,
        ref,
    ) => {
        const { palette } = useTheme();
        const { enqueueSnackbar } = useSnackbar();

        const [errorTriggered, setErrorTriggered] = useState<boolean>(false);
        const [isAwaitingConfirmation, setIsAwaitingConfirmation] = useState(false);
        const [isProcessing, setIsProcessing] = useState(false);

        const timeoutRef = useRef<number | null>(null);
        const isExecutingRef = useRef(false);

        const propsForError = useMemo<Omit<FaOptionIconProps, 'icon'>>(
            () =>
                errorTriggered
                    ? {
                          iconProps: {
                              shake: true,
                              fade: true,
                          },
                          color: palette.error.main,
                      }
                    : {},
            [errorTriggered, palette],
        );

        const triggerShortError = useCallback(() => {
            setErrorTriggered(true);
            setTimeout(() => {
                setErrorTriggered(false);
            }, 500);
        }, []);

        const errorFeedback = useCallback(
            (error: unknown) => {
                const uniformError = handleError({ error });

                enqueueSnackbar(uniformError.message, {
                    variant: 'error',
                });

                triggerShortError();
            },
            [enqueueSnackbar, triggerShortError],
        );

        const resetConfirmation = useCallback(() => {
            setIsAwaitingConfirmation(false);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        }, []);

        const handleClickOutside = useCallback(
            (event: MouseEvent) => {
                const buttonElement = typeof ref === 'function' ? null : ref?.current;
                if (buttonElement && !buttonElement.contains(event.target as Node)) {
                    resetConfirmation();
                }
            },
            [ref, resetConfirmation],
        );

        useEffect(() => {
            if (isAwaitingConfirmation) {
                document.addEventListener('mousedown', handleClickOutside);
                timeoutRef.current = setTimeout(resetConfirmation, confirmationTimeout);
            }

            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
            };
        }, [isAwaitingConfirmation, handleClickOutside, resetConfirmation, confirmationTimeout]);

        const minimumDelay = (ms: number) =>
            new Promise((resolve) => {
                setTimeout(resolve, ms);
            });

        const handleEnhancedClick = useCallback(
            async (event: React.MouseEvent<HTMLButtonElement>) => {
                // Protection synchrone immédiate
                if (isExecutingRef.current) {
                    return;
                }

                try {
                    // Si pas de confirmation requise, exécution directe
                    if (!confirmation) {
                        isExecutingRef.current = true;
                        if (showProcessingState) setIsProcessing(true);

                        try {
                            await Promise.all([
                                onClick(event),
                                minimumDelay(minimumProcessingDelay),
                            ]);
                        } finally {
                            isExecutingRef.current = false;
                            if (showProcessingState) setIsProcessing(false);
                        }
                        return;
                    }

                    // Si confirmation requise mais pas encore en attente
                    if (!isAwaitingConfirmation) {
                        setIsAwaitingConfirmation(true);
                        return;
                    }

                    // Confirmation - deuxième clic
                    isExecutingRef.current = true;
                    if (showProcessingState) setIsProcessing(true);
                    resetConfirmation();

                    try {
                        await Promise.all([onClick(event), minimumDelay(minimumProcessingDelay)]);
                    } finally {
                        isExecutingRef.current = false;
                        if (showProcessingState) setIsProcessing(false);
                    }
                } catch (error) {
                    // Gérer les erreurs
                    errorFeedback(error);
                    isExecutingRef.current = false;
                    if (showProcessingState) setIsProcessing(false);
                    resetConfirmation();
                }
            },
            [
                confirmation,
                isAwaitingConfirmation,
                onClick,
                resetConfirmation,
                minimumProcessingDelay,
                showProcessingState,
                errorFeedback,
            ],
        );

        const getCurrentIcon = React.useCallback(() => {
            if (isProcessing) return processingIcon;
            if (isAwaitingConfirmation) return confirmationIcon;
            return icon;
        }, [confirmationIcon, icon, isAwaitingConfirmation, isProcessing, processingIcon]);

        // Adaptation des props selon le type d'icône actuelle
        const { faOptionIconProps } = useMemo(
            () => adaptIconProps(getCurrentIcon()),
            [getCurrentIcon],
        );

        const toolTipTitle = useMemo(() => {
            if (displayTooltip) {
                // Si en attente de confirmation, utiliser le tooltip de confirmation
                if (isAwaitingConfirmation) {
                    return confirmationTooltip;
                }

                // Déterminer le tooltip
                const currentIcon = getCurrentIcon();
                if (isNamedIcon(currentIcon) && currentIcon.displayName) {
                    return currentIcon.displayName;
                }
                if (isIconProp(currentIcon)) {
                    if (typeof currentIcon === 'string') {
                        return `Icône ${currentIcon}`;
                    }
                    if (Array.isArray(currentIcon)) {
                        return `Icône ${currentIcon[1]}`;
                    }
                    if (typeof currentIcon === 'object' && 'iconName' in currentIcon) {
                        return `Icône ${currentIcon.iconName}`;
                    }
                }
            }
            return undefined;
        }, [displayTooltip, isAwaitingConfirmation, getCurrentIcon, confirmationTooltip]);

        const iconButtonAriaLabel = useMemo(() => {
            const currentIcon = getCurrentIcon();
            if (isNamedIcon(currentIcon) && currentIcon.name) {
                return `icon-button-${currentIcon.name}`;
            }
            if (isIconProp(currentIcon)) {
                if (typeof currentIcon === 'string') {
                    return `icon-button-${currentIcon}`;
                }
                if (Array.isArray(currentIcon)) {
                    return `icon-button-${currentIcon[1]}`;
                }
                if (typeof currentIcon === 'object' && 'iconName' in currentIcon) {
                    return `icon-button-${currentIcon.iconName}`;
                }
            }
            return 'icon-button-unknown';
        }, [getCurrentIcon]);

        const enhancedIconOverwriteProps = useMemo(() => {
            const baseProps = { ...iconOverwriteProps };

            // Styles pour l'état de confirmation
            if (isAwaitingConfirmation && confirmationAnimation) {
                baseProps.sx = {
                    ...baseProps.sx,
                    animation: 'pulse 1s infinite',
                    '@keyframes pulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.7 },
                        '100%': { opacity: 1 },
                    },
                };
            }

            // Styles pour le processing (spinner)
            if (isProcessing) {
                baseProps.iconProps = {
                    ...baseProps.iconProps,
                    spin: true,
                };
            }

            const definitiveConfirmationHoverColor = confirmationHoverColor || palette.warning.main;
            const definitiveNormalHoverColor = normalHoverColor || palette.primary.dark;

            // Couleurs hover
            const hoverColor = isAwaitingConfirmation
                ? definitiveConfirmationHoverColor
                : definitiveNormalHoverColor;
            if (hoverColor) {
                baseProps.sx = {
                    ...baseProps.sx,
                    '&:hover': {
                        color: hoverColor,
                    },
                };
            }

            return baseProps;
        }, [
            confirmationAnimation,
            confirmationHoverColor,
            iconOverwriteProps,
            isAwaitingConfirmation,
            isProcessing,
            normalHoverColor,
            palette.primary.dark,
            palette.warning.main,
        ]);

        // Contenu du bouton avec ou sans wrapper selon disabled
        const buttonContent = (
            <IconButton
                ref={ref}
                data-testid="test-id-FlexyIconButton"
                aria-label={iconButtonAriaLabel}
                size="small"
                onClick={disabled ? undefined : handleEnhancedClick}
                disabled={disabled || isProcessing}
                {...buttonOverwriteProps}
            >
                <FaOptionIcon
                    disabled={disabled || isProcessing}
                    {...faOptionIconProps}
                    {...enhancedIconOverwriteProps}
                    {...propsForError}
                />
            </IconButton>
        );

        // Si disabled, on wrap dans un span pour éviter le warning MUI Tooltip
        if (disabled) {
            return (
                <Tooltip
                    title={toolTipTitle}
                    {...tooltipOverwriteProps}
                >
                    <span>{buttonContent}</span>
                </Tooltip>
            );
        }

        // Si pas disabled, on peut utiliser directement le Tooltip
        return (
            <Tooltip
                title={toolTipTitle}
                {...tooltipOverwriteProps}
            >
                {buttonContent}
            </Tooltip>
        );
    },
);
